import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Quick Actions v2 — compact, mobile-first action cards with animated
 * inline-SVG scenes (no Lottie / GIF). Visual only; click handlers are
 * passed through unchanged from the Dashboard.
 */

function spawnRipple(e) {
  const el = e.currentTarget;
  const rect = el.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height) * 1.2;
  const span = document.createElement("span");
  span.className = "qa-ripple";
  span.style.width = `${size}px`;
  span.style.height = `${size}px`;
  span.style.left = `${e.clientX - rect.left - size / 2}px`;
  span.style.top = `${e.clientY - rect.top - size / 2}px`;
  el.appendChild(span);
  setTimeout(() => span.remove(), 620);
}

/* ─────────────────────────────  SVG scenes  ───────────────────────────── */

function SceneIncome() {
  return (
    <svg viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
      {/* Sparkles */}
      <g className="qa2-sparkle" style={{ animationDelay: "0.1s" }}>
        <path d="M9 12 L11 14 L9 16 L7 14 Z" fill="#FBBF24" />
      </g>
      <g className="qa2-sparkle" style={{ animationDelay: "0.8s" }}>
        <path d="M46 9 L48 11 L46 13 L44 11 Z" fill="#FBBF24" />
      </g>
      {/* Falling coins */}
      <g className="qa2-coin-in" style={{ animationDelay: "0s" }}>
        <circle cx="20" cy="10" r="4" fill="#FCD34D" stroke="#B45309" strokeWidth="0.8" />
        <text x="20" y="13" textAnchor="middle" fontSize="6" fontWeight="800" fill="#92400E">$</text>
      </g>
      <g className="qa2-coin-in" style={{ animationDelay: "0.6s" }}>
        <circle cx="32" cy="10" r="3.5" fill="#FCD34D" stroke="#B45309" strokeWidth="0.8" />
        <text x="32" y="12.6" textAnchor="middle" fontSize="5" fontWeight="800" fill="#92400E">$</text>
      </g>
      <g className="qa2-coin-in" style={{ animationDelay: "1.2s" }}>
        <circle cx="26" cy="10" r="3" fill="#FCD34D" stroke="#B45309" strokeWidth="0.8" />
      </g>
      {/* Wallet body */}
      <rect x="10" y="28" width="36" height="22" rx="5" fill="#047857" stroke="#065F46" strokeWidth="1" />
      <rect x="10" y="28" width="36" height="8" rx="5" fill="#10B981" />
      {/* Wallet flap (animated) */}
      <g className="qa2-flap">
        <path d="M14 31 L42 31 L40 25 L16 25 Z" fill="#34D399" stroke="#065F46" strokeWidth="0.8" />
      </g>
      {/* Clasp */}
      <circle cx="38" cy="40" r="2.4" fill="#FCD34D" stroke="#B45309" strokeWidth="0.6" />
    </svg>
  );
}

function SceneExpense() {
  return (
    <svg viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
      {/* Wallet at top */}
      <rect x="10" y="6" width="36" height="20" rx="5" fill="#B91C1C" stroke="#7F1D1D" strokeWidth="1" />
      <rect x="10" y="6" width="36" height="7" rx="5" fill="#EF4444" />
      <g className="qa2-flap">
        <path d="M14 9 L42 9 L40 4 L16 4 Z" fill="#FCA5A5" stroke="#7F1D1D" strokeWidth="0.8" />
      </g>
      <circle cx="38" cy="17" r="2.4" fill="#FBBF24" stroke="#7C2D12" strokeWidth="0.6" />
      {/* Coins falling OUT */}
      <g className="qa2-coin-out" style={{ animationDelay: "0s" }}>
        <circle cx="20" cy="30" r="3.5" fill="#FCD34D" stroke="#B45309" strokeWidth="0.8" />
        <text x="20" y="32.6" textAnchor="middle" fontSize="5" fontWeight="800" fill="#92400E">$</text>
      </g>
      <g className="qa2-coin-out" style={{ animationDelay: "0.5s" }}>
        <circle cx="30" cy="30" r="3" fill="#FCD34D" stroke="#B45309" strokeWidth="0.8" />
      </g>
      <g className="qa2-coin-out" style={{ animationDelay: "1.1s" }}>
        <circle cx="38" cy="30" r="3.5" fill="#FCD34D" stroke="#B45309" strokeWidth="0.8" />
        <text x="38" y="32.6" textAnchor="middle" fontSize="5" fontWeight="800" fill="#92400E">$</text>
      </g>
    </svg>
  );
}

function SceneVoice() {
  return (
    <svg viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
      {/* Expanding rings */}
      <circle cx="28" cy="28" r="10" fill="none" stroke="#60A5FA" strokeWidth="1.5" className="qa2-ring" style={{ animationDelay: "0s" }} />
      <circle cx="28" cy="28" r="10" fill="none" stroke="#60A5FA" strokeWidth="1.5" className="qa2-ring" style={{ animationDelay: "0.6s" }} />
      <circle cx="28" cy="28" r="10" fill="none" stroke="#60A5FA" strokeWidth="1.5" className="qa2-ring" style={{ animationDelay: "1.2s" }} />
      {/* Mic */}
      <rect x="24" y="18" width="8" height="14" rx="4" fill="#1E3A8A" stroke="#FFFFFF" strokeWidth="0.8" />
      <path d="M20 28 Q20 36 28 36 Q36 36 36 28" fill="none" stroke="#1E3A8A" strokeWidth="2" strokeLinecap="round" />
      <rect x="26" y="36" width="4" height="4" fill="#1E3A8A" />
      <rect x="22" y="40" width="12" height="2" rx="1" fill="#1E3A8A" />
      {/* Voice bars (tiny side) */}
      <rect x="8"  y="26" width="2" height="4"  rx="1" fill="#93C5FD" />
      <rect x="46" y="26" width="2" height="4"  rx="1" fill="#93C5FD" />
    </svg>
  );
}

function SceneScan() {
  return (
    <svg viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
      {/* Receipt */}
      <rect x="14" y="10" width="28" height="36" rx="2" fill="#FFFFFF" stroke="#6D28D9" strokeWidth="1.2" />
      <path d="M14 46 L18 44 L22 46 L26 44 L30 46 L34 44 L38 46 L42 44 L42 46 Z" fill="#FFFFFF" stroke="#6D28D9" strokeWidth="1.2" />
      {/* Receipt lines */}
      <rect x="18" y="14" width="20" height="2" rx="0.5" fill="#C4B5FD" />
      <rect x="18" y="18" width="14" height="1.5" rx="0.5" fill="#DDD6FE" />
      <rect x="18" y="22" width="16" height="1.5" rx="0.5" fill="#DDD6FE" />
      <rect x="18" y="28" width="20" height="1.5" rx="0.5" fill="#DDD6FE" />
      <rect x="18" y="32" width="12" height="1.5" rx="0.5" fill="#DDD6FE" />
      {/* Scanning laser */}
      <g className="qa2-scan-line">
        <rect x="13" y="14" width="30" height="2" fill="#10B981" opacity="0.85">
          <animate attributeName="opacity" values="0.6;1;0.6" dur="1s" repeatCount="indefinite" />
        </rect>
      </g>
      {/* Corner brackets */}
      <path d="M11 10 L11 14 M11 10 L15 10"  stroke="#9333EA" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M45 10 L45 14 M45 10 L41 10"  stroke="#9333EA" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M11 46 L11 42 M11 46 L15 46"  stroke="#9333EA" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M45 46 L45 42 M45 46 L41 46"  stroke="#9333EA" strokeWidth="1.6" strokeLinecap="round" />
      {/* Success tick */}
      <g className="qa2-check">
        <circle cx="40" cy="40" r="6.5" fill="#10B981" stroke="#FFFFFF" strokeWidth="1.4" />
        <path d="M37 40 L39.3 42.3 L43 38.5" fill="none" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

function SceneAI() {
  return (
    <svg viewBox="0 0 80 56" width="80" height="56" aria-hidden="true">
      {/* Mini chart card behind */}
      <g className="qa2-orbit-a">
        <rect x="6" y="14" width="22" height="14" rx="3" fill="#FFFFFF" opacity="0.95" />
        <polyline points="9,25 13,19 17,22 21,15 25,18" fill="none" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="25" cy="18" r="1.4" fill="#6366F1" />
      </g>
      {/* Coin */}
      <g className="qa2-orbit-b">
        <circle cx="64" cy="40" r="6" fill="#FCD34D" stroke="#B45309" strokeWidth="1" />
        <text x="64" y="43" textAnchor="middle" fontSize="7" fontWeight="800" fill="#92400E">$</text>
      </g>
      {/* AI head */}
      <g className="qa2-breathe">
        <rect x="32" y="14" width="20" height="22" rx="6" fill="url(#aiHead)" stroke="#FFFFFF" strokeWidth="1.2" />
        <circle cx="38" cy="24" r="2" fill="#FFFFFF" />
        <circle cx="46" cy="24" r="2" fill="#FFFFFF" />
        <circle cx="38" cy="24" r="0.9" fill="#1E1B4B" />
        <circle cx="46" cy="24" r="0.9" fill="#1E1B4B" />
        <path d="M38 30 Q42 33 46 30" fill="none" stroke="#FFFFFF" strokeWidth="1.4" strokeLinecap="round" />
        {/* antenna */}
        <line x1="42" y1="10" x2="42" y2="14" stroke="#FFFFFF" strokeWidth="1.4" strokeLinecap="round" />
        <circle cx="42" cy="9" r="2" fill="#FBBF24" />
      </g>
      {/* Sparkles */}
      <g className="qa2-sparkle" style={{ animationDelay: "0.2s" }}>
        <path d="M58 8 L60 10 L58 12 L56 10 Z" fill="#FBBF24" />
      </g>
      <g className="qa2-sparkle" style={{ animationDelay: "0.9s" }}>
        <path d="M24 44 L26 46 L24 48 L22 46 Z" fill="#FBBF24" />
      </g>
      <defs>
        <linearGradient id="aiHead" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"  stopColor="#6366F1" />
          <stop offset="100%" stopColor="#8B5CF6" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/* ─────────────────────────────  Card component  ───────────────────────────── */

function ActionCard({
  as = "button", to, onClick, title, subtitle,
  scene, gradient, glow, accent, ariaLabel, testid, hero = false,
}) {
  const handleClick = (e) => { spawnRipple(e); onClick?.(e); };

  const classes =
    `qa2-card group flex w-full items-center gap-3 rounded-[20px] border border-white/15 ` +
    `${hero ? "px-4 py-3.5 lg:px-5 lg:py-4" : "px-3 py-2.5 lg:px-3.5 lg:py-3"} ` +
    `text-left text-white backdrop-blur-[4px]`;

  const style = {
    background: gradient,
    boxShadow: `0 14px 28px -14px ${glow}, inset 0 1px 0 rgba(255,255,255,0.18)`,
    "--qa2-glow": glow,
  };

  const inner = (
    <>
      <div className={`qa2-scene-wrap flex shrink-0 items-center justify-center ${hero ? "h-12 w-16 lg:h-12 lg:w-16" : "h-12 w-12 lg:h-13 lg:w-13"}`}>
        {scene}
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className={`font-display font-bold leading-tight tracking-[-0.01em] ${hero ? "text-[17px] lg:text-[15.5px]" : "text-[15px] lg:text-[15.5px]"}`}>
          {title}
        </div>
        <div className={`mt-0.5 font-medium leading-snug text-white/85 ${hero ? "text-[12.5px] lg:text-[11.5px]" : "text-[11.5px] lg:text-[12px]"} ${hero ? "lg:truncate" : ""}`}>
          {subtitle}
        </div>
      </div>
      <span
        aria-hidden
        className={`qa2-cta inline-flex shrink-0 items-center justify-center rounded-full ${hero ? "h-9 w-9" : "h-8 w-8"}`}
        style={{
          background: accent,
          boxShadow: `0 6px 14px -6px ${glow}, inset 0 1px 0 rgba(255,255,255,0.35)`,
        }}
      >
        <ArrowRight className={hero ? "h-4 w-4" : "h-3.5 w-3.5"} strokeWidth={2.5} />
      </span>
    </>
  );

  if (as === "link") {
    return (
      <Link to={to} onClick={handleClick} className={classes} style={style} data-testid={testid} aria-label={ariaLabel || `${title}, ${subtitle}`}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={handleClick} className={classes} style={style} data-testid={testid} aria-label={ariaLabel || `${title}, ${subtitle}`}>
      {inner}
    </button>
  );
}

/* ─────────────────────────────  Section  ───────────────────────────── */

export default function QuickActions({ onVoice, onScan, onIncome, onExpense }) {
  const { t } = useTranslation();
  return (
    <section
      aria-labelledby="quick-actions-title"
      data-testid="quick-actions-section"
      className="flex flex-col items-center text-center"
    >
      {/* Header */}
      <header className="qa-enter qa-enter-1 flex flex-col items-center">
        <h2
          id="quick-actions-title"
          className="font-display text-[22px] font-extrabold leading-tight tracking-tight md:text-[26px]"
        >
          {t("quick_actions")}
        </h2>
        <p className="mt-1.5 max-w-[480px] text-[13px] font-medium leading-snug text-muted-foreground md:text-[13.5px]">
          {t("quick_actions_subtitle")}
        </p>
      </header>

      {/* Grid: 2 cols on mobile/tablet; 5 equal cols on desktop. AI spans full width below 5-col. */}
      <div
        role="toolbar"
        aria-label={t("quick_actions")}
        className="qa-enter qa-enter-2 mt-5 grid w-full max-w-[1080px] grid-cols-2 gap-3 lg:grid-cols-5 lg:gap-3.5"
      >
        <ActionCard
          title={t("qa_income")}
          subtitle={t("add_income")}
          scene={<SceneIncome />}
          gradient="linear-gradient(135deg, #047857 0%, #10B981 50%, #16A34A 100%)"
          glow="rgba(22,163,74,0.55)"
          accent="rgba(255,255,255,0.22)"
          onClick={onIncome}
          testid="quick-add-income-button"
          ariaLabel={t("add_income")}
        />
        <ActionCard
          title={t("qa_expense")}
          subtitle={t("add_expense")}
          scene={<SceneExpense />}
          gradient="linear-gradient(135deg, #B91C1C 0%, #DC2626 50%, #EF4444 100%)"
          glow="rgba(239,68,68,0.55)"
          accent="rgba(255,255,255,0.22)"
          onClick={onExpense}
          testid="quick-add-expense-button"
          ariaLabel={t("add_expense")}
        />
        <ActionCard
          title={t("qa_voice")}
          subtitle={t("qa_voice_sub")}
          scene={<SceneVoice />}
          gradient="linear-gradient(135deg, #1E3A8A 0%, #2563EB 100%)"
          glow="rgba(37,99,235,0.55)"
          accent="rgba(255,255,255,0.22)"
          onClick={onVoice}
          testid="quick-voice-button"
          ariaLabel={t("qa_voice_sub")}
        />
        <ActionCard
          title={t("qa_scan")}
          subtitle={t("qa_scan_sub")}
          scene={<SceneScan />}
          gradient="linear-gradient(135deg, #6D28D9 0%, #8B5CF6 50%, #9333EA 100%)"
          glow="rgba(147,51,234,0.55)"
          accent="rgba(255,255,255,0.22)"
          onClick={onScan}
          testid="quick-receipt-button"
          ariaLabel={t("qa_scan_sub")}
        />
        {/* AI Assistant — hero / full-width on mobile and tablet */}
        <div className="col-span-2 lg:col-span-1">
          <ActionCard
            as="link"
            to="/insights"
            title={t("qa_ai")}
            subtitle={t("qa_ai_sub")}
            scene={<SceneAI />}
            gradient="linear-gradient(135deg, #2563EB 0%, #6366F1 45%, #8B5CF6 100%)"
            glow="rgba(99,102,241,0.65)"
            accent="rgba(255,255,255,0.25)"
            hero
            testid="quick-insights-button"
            ariaLabel={t("qa_ai")}
          />
        </div>
      </div>
    </section>
  );
}
